import { useBlocker } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { ApiError } from '~/api/client'
import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { SEND_TIMING } from '~/components/feedback-timing'
import { CheckIcon, SearchIcon } from '~/components/icons'
import { useToast } from '~/components/toast-context'
import { useApi } from '~/data/data-context'
import { FEEDBACK_STATUSES, type Feedback, type FeedbackStatus } from '~/data/types'
import { formatDayLabel } from '~/lib/dates'
import { TimedOut, withTimeout } from '~/lib/with-timeout'

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
  rejected: 'Rejected',
}

/**
 * Telling us what to fix — one field and a way to send it, per `/specs/features/feedback.spec.md`,
 * in the corner card. An admin can turn the same panel over to reading what everybody else sent.
 *
 * What was typed lives here rather than in the sending half, because every way out of the
 * panel has to know whether there is anything to lose — including the ones that leave from
 * the managing half, where the words are out of sight but not gone.
 */
export function FeedbackPanel({ onBack }: { onBack: () => void }) {
  const [managing, setManaging] = useState(false)
  const [body, setBody] = useState('')

  /*
   * Feedback has an address of its own, so every way out of it is a navigation — Escape, a
   * click elsewhere, the browser's own back button. That is one thing to guard rather than
   * three, and guarding the navigation catches the back button, which no control here would
   * have.
   */
  const holding = useRef(false)
  useEffect(() => {
    holding.current = body.trim() !== ''
  }, [body])

  const leaving = useBlocker({
    shouldBlockFn: ({ next }) => holding.current && next.search.dialog !== 'feedback',
    // The words are the app's to look after while it is open; a reload is the browser's own
    // business, and a native prompt over it is not this panel's to raise.
    enableBeforeUnload: false,
    withResolver: true,
  })

  return (
    <>
      {managing ? (
        <ManageFeedback
          onBack={() => {
            setManaging(false)
          }}
        />
      ) : (
        <SendFeedback
          body={body}
          onBody={setBody}
          onManage={() => {
            setManaging(true)
          }}
          onSent={() => {
            holding.current = false
            onBack()
          }}
          onBack={onBack}
        />
      )}

      {leaving.status === 'blocked' ? (
        <ConfirmDialog
          title="Discard your feedback?"
          body="What you have written will be lost."
          confirm="Discard"
          onConfirm={() => {
            holding.current = false
            leaving.proceed()
          }}
          onDismiss={leaving.reset}
        />
      ) : null}
    </>
  )
}

function SendFeedback({
  body,
  onBody,
  onManage,
  onSent,
  onBack,
}: {
  body: string
  onBody: (body: string) => void
  onManage: () => void
  /** Sent, so there is nothing left to lose and the panel just goes. */
  onSent: () => void
  onBack: () => void
}) {
  const api = useApi()
  const { session } = useAuth()
  const toast = useToast()
  const [sending, setSending] = useState(false)
  const sendingShown = useBeat(sending)
  const field = useRef<HTMLTextAreaElement>(null)

  // The panel exists to be typed into, so it opens ready for typing.
  useEffect(() => {
    field.current?.focus()
  }, [])

  const send = async () => {
    if (sending) return
    setSending(true)
    try {
      await withTimeout(api.sendFeedback(body), SEND_TIMING.timeoutMs)
      // The panel is gone by the time this is read, which is why it is a toast: how it
      // went is said over whatever is on screen when it lands.
      toast('Feedback sent. Thank you.')
      onSent()
    } catch (error) {
      // Refused, or never reached us. Either way the words stay in the field.
      toast(
        error instanceof TimedOut
          ? 'That took too long to send. It may not have reached us — try again in a moment.'
          : error instanceof ApiError && error.status === 429
            ? 'That is as much feedback as one hour holds. Try again a little later.'
            : 'That did not reach us. Try again in a moment.',
      )
      setSending(false)
    }
  }

  return (
    <>
      <PanelHeader title="Feedback" onBack={onBack}>
        {session?.user.isAdmin ? (
          <button type="button" className="pr-2 text-sm text-accent" onClick={onManage}>
            Manage
          </button>
        ) : null}
      </PanelHeader>
      <div className="w-96 space-y-3 px-4 pb-4">
        <textarea
          ref={field}
          aria-label="How can we improve?"
          placeholder="How can we improve?"
          rows={5}
          value={body}
          onChange={(event) => {
            onBody(event.target.value)
          }}
          onKeyDown={(event) => {
            // Ctrl+Enter and ⌘+Enter send under the same guards the control presses with.
            if (
              event.key === 'Enter' &&
              (event.ctrlKey || event.metaKey) &&
              body.trim() !== '' &&
              !sending
            ) {
              event.preventDefault()
              void send()
            }
          }}
          className="w-full resize-y rounded-2xl bg-surface p-4 text-base outline-none ring-1 ring-white/10 placeholder:text-ink-muted focus:ring-2 focus:ring-accent"
        />
        <div className="flex justify-end">
          <button
            type="button"
            aria-busy={sending || undefined}
            disabled={!sending && body.trim() === ''}
            className="relative rounded-full bg-accent px-4 py-2 text-sm font-medium text-on-accent disabled:opacity-50"
            onClick={() => {
              void send()
            }}
          >
            <Working shown={sendingShown}>Send</Working>
          </button>
        </div>
      </div>
    </>
  )
}

function ManageFeedback({ onBack }: { onBack: () => void }) {
  const api = useApi()
  const [feedback, setFeedback] = useState<Feedback[] | null>(null)
  const [term, setTerm] = useState('')
  // Every status is on to begin with, so managing opens on the whole of it.
  const [shown, setShown] = useState<readonly FeedbackStatus[]>(FEEDBACK_STATUSES)

  useEffect(() => {
    let cancelled = false
    api
      .listFeedback()
      .then((sent) => {
        if (!cancelled) {
          setFeedback(sent)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFeedback([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [api])

  const changeStatus = async (id: string, status: FeedbackStatus) => {
    setFeedback((current) =>
      (current ?? []).map((item) => (item.id === id ? { ...item, status } : item)),
    )
    try {
      await api.setFeedbackStatus(id, status)
    } catch {
      // The list is refetched next time it opens; nothing here is the record.
    }
  }

  const needle = term.trim().toLowerCase()
  const listed = (feedback ?? []).filter(
    (item) =>
      shown.includes(item.status) &&
      (needle === '' ||
        item.body.toLowerCase().includes(needle) ||
        item.fromName.toLowerCase().includes(needle) ||
        item.fromEmail.toLowerCase().includes(needle)),
  )

  return (
    <>
      <PanelHeader title="Feedback sent in" onBack={onBack} backLabel="Send feedback" />
      <div className="w-112 space-y-3 px-4 pb-4">
        <label className="flex items-center gap-3 rounded-full bg-surface px-4 py-2">
          <SearchIcon aria-hidden className="size-6 shrink-0 text-ink-muted" />
          <input
            type="search"
            aria-label="Search feedback"
            placeholder="Search feedback"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
            }}
            className="w-full bg-transparent text-sm outline-none placeholder:text-ink-muted"
          />
        </label>

        <div className="flex flex-wrap gap-2">
          {FEEDBACK_STATUSES.map((status) => {
            const on = shown.includes(status)
            return (
              <button
                key={status}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setShown((current) =>
                    on
                      ? current.filter((held) => held !== status)
                      : FEEDBACK_STATUSES.filter(
                          (held) => held === status || current.includes(held),
                        ),
                  )
                }}
                className={`flex h-8 items-center gap-1.5 rounded-full px-3.5 text-sm transition-colors ${
                  on
                    ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
                    : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
                }`}
              >
                {on && <CheckIcon className="-ml-1 h-6 w-6 shrink-0" />}
                {STATUS_LABELS[status]}
              </button>
            )
          })}
        </div>

        <ul className="space-y-2">
          {listed.map((item) => (
            <li key={item.id} className="space-y-2 rounded-2xl bg-surface p-4">
              <p className="text-sm whitespace-pre-wrap">{item.body}</p>
              <div className="flex flex-wrap items-center gap-3">
                <p className="min-w-0 flex-1 truncate text-xs text-ink-muted">
                  {item.fromName} · {item.fromEmail} · {formatDayLabel(item.createdAt)}
                </p>
                <select
                  aria-label={`Status of the feedback from ${item.fromName}`}
                  value={item.status}
                  onChange={(event) => {
                    void changeStatus(item.id, event.target.value as FeedbackStatus)
                  }}
                  className="rounded-lg bg-surface-bright px-2 py-1 text-xs text-ink outline-none"
                >
                  {FEEDBACK_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
        {feedback !== null && listed.length === 0 ? (
          <p className="px-1 py-4 text-center text-sm text-ink-muted">Nothing to read here.</p>
        ) : null}
      </div>
    </>
  )
}
